/**
 * Assistant surface contract — how open modals/forms expose actions to the
 * assistant's tools.
 *
 * A "surface" (e.g. the Create Booking modal) registers a set of named
 * handlers while it is open. Tool executors call askSurface() and get the
 * handler's result string back — or null when no surface is registered (the
 * form isn't open) or the handler doesn't answer in time.
 *
 * This replaces the bespoke per-tool CustomEvents + respond() callbacks:
 * a plain module-level handler map, no window events involved.
 */

export type AssistantSurfaceHandler = (
  payload: Record<string, unknown>,
) => Promise<string> | string;

interface AssistantSurface {
  handlers: Record<string, AssistantSurfaceHandler>;
}

const surfaces = new Map<string, AssistantSurface>();

/**
 * Register a surface's handlers while it is open. Returns an unregister
 * function — call it on unmount/close (return it from a useEffect).
 * Handlers should read current component state via a ref that is reassigned
 * every render, exactly like the existing aiBridgeRef pattern.
 */
export function registerAssistantSurface(
  id: string,
  handlers: Record<string, AssistantSurfaceHandler>,
): () => void {
  const surface: AssistantSurface = { handlers };
  surfaces.set(id, surface);
  return () => {
    // Only remove our own registration (a newer instance may have replaced it)
    if (surfaces.get(id) === surface) surfaces.delete(id);
  };
}

/**
 * Surfaces that are always registered (app-wide capabilities like the phone),
 * not open forms — excluded from the "forms open on screen" report.
 */
const PERSISTENT_SURFACES = new Set(['phone', 'messages']);

/**
 * IDs of the surfaces (open forms) currently registered — sent with each
 * ai-help request so the model knows, per turn, that an unsaved form is open
 * and follow-up tweaks must go through its form tools.
 */
export function getOpenAssistantSurfaces(): string[] {
  return Array.from(surfaces.keys()).filter((id) => !PERSISTENT_SURFACES.has(id));
}

/**
 * Ask an open surface to perform an action. Resolves to the handler's result
 * string, or null when the surface isn't registered (form not open), the
 * action is unknown, or the handler doesn't resolve within timeoutMs.
 */
export async function askSurface(
  id: string,
  action: string,
  payload: Record<string, unknown>,
  timeoutMs = 5000,
): Promise<string | null> {
  const handler = surfaces.get(id)?.handlers[action];
  if (!handler) return null;
  try {
    const result = await Promise.race([
      Promise.resolve(handler(payload)),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    return typeof result === 'string' ? result : null;
  } catch (err) {
    console.warn(`[assistant] surface "${id}" action "${action}" failed:`, err);
    return `error: the action failed unexpectedly — ${err instanceof Error ? err.message : 'unknown error'}.`;
  }
}
